import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { API_CONFIG } from "config/api.config";
import { logout as logoutAction } from "../store/slices/auth/slice";

// Type guards
const isFormData = (v) => typeof FormData !== "undefined" && v instanceof FormData;
const isFile = (v) => typeof File !== "undefined" && v instanceof File;
const isBlob = (v) => typeof Blob !== "undefined" && v instanceof Blob;
const isPlainObject = (v) => v && typeof v === "object" && !Array.isArray(v) && !(v instanceof Date) && !isFormData(v) && !isFile(v) && !isBlob(v);

// Convert camelCase to snake_case
export const toSnakeKey = (key) => key.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);

// Convert JSON object to snake_case recursively
export const toSnakeCaseJson = (value) => {
  if (value === null || typeof value !== "object" || value instanceof Date || isFormData(value) || isFile(value) || isBlob(value)) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((i) => toSnakeCaseJson(i));
  }

  const out = {};
  for (const key of Object.keys(value)) {
    const snake = toSnakeKey(key);
    const v = value[key];
    out[snake] = isPlainObject(v) || Array.isArray(v) ? toSnakeCaseJson(v) : v;
  }
  return out;
};

// Convert FormData keys to snake_case
export const toSnakeCaseFormData = (fdIn) => {
  const fdOut = new FormData();
  for (const [key, val] of fdIn.entries()) {
    fdOut.append(toSnakeKey(key), val);
  }
  return fdOut;
};

// Prepare request body with snake_case conversion
export const prepareRequest = (args) => {
  if (!args || !args.body) return args;

  if (isFormData(args.body)) {
    return { ...args, body: toSnakeCaseFormData(args.body) };
  }

  if (isPlainObject(args.body) || Array.isArray(args.body)) {
    return { ...args, body: toSnakeCaseJson(args.body) };
  }

  return args;
};

// Create base API with automatic token refresh
export const createBaseApi = (tagType, basePath = "") => {
  const baseQuery = fetchBaseQuery({
    baseUrl: `${API_CONFIG.BACKEND_URL}${basePath}`,
    credentials: "include",
  });

  const rootQuery = fetchBaseQuery({
    baseUrl: API_CONFIG.BACKEND_URL,
    credentials: "include",
  });

  const baseQueryWithReauth = async (args, api, extraOptions) => {
    const modifiedArgs = prepareRequest(args);
    let result = await baseQuery(modifiedArgs, api, extraOptions);

    // Handle 401 errors with token refresh
    if (result.error?.status === 401 && !window.isRefreshing && result.error?.data?.error !== "invalid_credentials") {
      window.isRefreshing = true;

      try {
        if (!window.refreshPromise) {
          window.refreshPromise = rootQuery({ url: "/auth/refresh-token", method: "POST" }, api, extraOptions);
        }

        const refresh = await window.refreshPromise;

        if (refresh.data?.success) {
          // Retry with the modified args (snake_case converted)
          result = await baseQuery(modifiedArgs, api, extraOptions);
        } else {
          // Refresh failed (e.g. refresh token expired)
          api.dispatch(logoutAction());
          
          // Only redirect if we were actually logged in or trying to access a protected route
          if (!window.location.pathname.startsWith('/auth') && window.location.pathname !== '/') {
             window.location.href = "/?skipInit=true";
          }
        }
      } catch (e) {
        api.dispatch(logoutAction());
        if (!window.location.pathname.startsWith('/auth') && window.location.pathname !== '/') {
           window.location.href = "/?skipInit=true";
        }
      } finally {
        window.isRefreshing = false;
        window.refreshPromise = null;
      }
    }

    return result;
  };

  return createApi({
    reducerPath: `${tagType}Api`,
    baseQuery: baseQueryWithReauth,
    tagTypes: [tagType],
    endpoints: () => ({}),
  });
};

export const createBaseQuery = (basePath = "") =>
  fetchBaseQuery({
    baseUrl: `${API_CONFIG.BACKEND_URL}${basePath}`,
    credentials: "include",
  });
